import { useState, useEffect, useCallback, useRef } from 'react';

const TTL_MS = 86400000; // 24 hours

/**
 * Custom hook for sessionStorage-based draft persistence with TTL, debounced writes,
 * flush-on-unmount, QuotaExceededError handling, and logout cleanup.
 *
 * @param {string} dialogKey - Unique identifier for the dialog
 * @param {object} defaultDraft - Default draft values when no stored draft exists
 * @param {object} options - Optional configuration
 * @param {function} [options.serialize] - Custom serialization before writing to sessionStorage
 * @param {function} [options.deserialize] - Custom deserialization after reading from sessionStorage
 * @returns {{ draft: object, setDraft: function, clearDraft: function, hasDraft: boolean, isExpired: boolean }}
 */
const useDialogDraft = (dialogKey, defaultDraft = {}, options = {}) => {
  const { serialize, deserialize } = options;

  const isBrowser = typeof window !== 'undefined';

  const getUsername = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.username || 'anonymous';
    } catch {
      return 'anonymous';
    }
  };

  // Computed dynamically (not memoized) to handle account switching
  const getStorageKey = () => {
    const username = getUsername();
    return `draft_v1_${dialogKey}_${username}`;
  };

  const loadDraft = () => {
    if (!isBrowser) return { data: defaultDraft, expired: false };

    try {
      const key = getStorageKey();
      const stored = sessionStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        const timestamp = parsed._timestamp;

        if (timestamp && Date.now() - timestamp > TTL_MS) {
          sessionStorage.removeItem(key);
          return { data: defaultDraft, expired: true };
        }

        const restoredDraft = deserialize ? deserialize(parsed) : parsed;
        // eslint-disable-next-line no-unused-vars
        const { _timestamp, ...draftData } = restoredDraft;
        return { data: { ...defaultDraft, ...draftData }, expired: false };
      }
    } catch {
      try {
        sessionStorage.removeItem(getStorageKey());
      } catch { /* corrupted key, best effort remove */ }
    }

    return { data: defaultDraft, expired: false };
  };

  const initial = loadDraft();
  const [draft, setDraftState] = useState(initial.data);
  const [isExpired, setIsExpired] = useState(initial.expired);

  const draftRef = useRef(draft);
  draftRef.current = draft;

  const defaultDraftSnapshotRef = useRef(JSON.stringify(defaultDraft));

  const isEquivalentToDefault = (data) => {
    return JSON.stringify(data) === defaultDraftSnapshotRef.current;
  };

  const computeHasDraft = () => {
    if (!isBrowser) return false;
    try {
      const key = getStorageKey();
      const stored = sessionStorage.getItem(key);
      if (!stored) return false;
      const parsed = JSON.parse(stored);
      const timestamp = parsed._timestamp;
      if (timestamp && Date.now() - timestamp > TTL_MS) {
        sessionStorage.removeItem(key);
        return false;
      }
      // eslint-disable-next-line no-unused-vars
      const { _timestamp, ...draftData } = parsed;
      if (isEquivalentToDefault(draftData)) {
        sessionStorage.removeItem(key);
        return false;
      }
      return true;
    } catch {
      return false;
    }
  };

  const [hasDraft, setHasDraft] = useState(computeHasDraft);

  const isUnmountingRef = useRef(false);

  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
    };
  }, []);

  useEffect(() => {
    isUnmountingRef.current = false;

    const saveDraft = () => {
      if (!isBrowser) return;
      try {
        const key = getStorageKey();
        const dataToSave = serialize ? serialize(draft) : draft;
        if (isEquivalentToDefault(dataToSave)) {
          sessionStorage.removeItem(key);
          return;
        }
        sessionStorage.setItem(key, JSON.stringify({ ...dataToSave, _timestamp: Date.now() }));
      } catch (error) {
        if (error.name === 'QuotaExceededError') {
          sessionStorage.removeItem(getStorageKey());
        }
      }
    };

    const timer = setTimeout(saveDraft, 500);

    return () => {
      clearTimeout(timer);
      if (!isUnmountingRef.current || !isBrowser) return;
      try {
        const key = getStorageKey();
        const dataToSave = serialize ? serialize(draftRef.current) : draftRef.current;
        if (isEquivalentToDefault(dataToSave)) {
          sessionStorage.removeItem(key);
          return;
        }
        sessionStorage.setItem(key, JSON.stringify({ ...dataToSave, _timestamp: Date.now() }));
      } catch (error) {
        if (error.name === 'QuotaExceededError') {
          sessionStorage.removeItem(getStorageKey());
        }
      }
    };
  }, [draft, dialogKey, serialize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setHasDraft(computeHasDraft());
  }, [draft, dialogKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDraft = useCallback((updater) => {
    setDraftState((prev) => {
      const newDraft = typeof updater === 'function' ? updater(prev) : updater;
      return newDraft;
    });
  }, []);

  const clearDraft = useCallback(() => {
    if (isBrowser) {
      try {
        sessionStorage.removeItem(getStorageKey());
      } catch { /* best effort */ }
    }
    setDraftState(defaultDraft);
    setIsExpired(false);
  }, [dialogKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleLogout = () => {
      if (!isBrowser) return;
      clearDraft();
    };

    window.addEventListener('userLogout', handleLogout);
    return () => {
      window.removeEventListener('userLogout', handleLogout);
    };
  }, [clearDraft, isBrowser]);

  return { draft, setDraft, clearDraft, hasDraft, isExpired };
};

export { useDialogDraft };
export default useDialogDraft;